import * as actionType from './actionTypes';

export const initialState = {
  products: [],
  productsLoading: false,
  productsError: null,
  search: { productCode: '', vin: '' },
  transactions: [],
  transactionsLoading: false,
  transactionsError: null,
  selectedId: null,
  // Results and the order they belong to are kept in a single object, so the
  // results on screen can never point at a different transaction than the
  // force target.
  evaluation: null,
  evaluationLoading: false,
  evaluationError: null,
  forceResult: null,
  // Order a guarantee has already been created for. Kept outside
  // `clearedEvaluation` so stepping back and re-running the rules cannot lift
  // the guard.
  forcedOrderId: null,
  forceLoading: false,
  forceError: null,
  modalOpen: false,
};

// Everything downstream of the eligibility run.
const clearedEvaluation = {
  evaluation: null,
  evaluationError: null,
  forceResult: null,
  forceError: null,
};

const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case actionType.SET_PRODUCTS:
      return { ...state, products: action.payload, productsLoading: false };
    case actionType.SET_PRODUCTS_LOADING:
      // Starting a load clears the previous error; stopping it preserves one.
      return {
        ...state,
        productsLoading: action.payload,
        productsError: action.payload ? null : state.productsError,
      };
    case actionType.SET_PRODUCTS_ERROR:
      return {
        ...state,
        productsError: action.payload,
        productsLoading: false,
      };

    // A new lookup invalidates the whole flow below it.
    case actionType.SET_SEARCH:
      return {
        ...state,
        ...clearedEvaluation,
        search: action.payload,
        transactions: [],
        transactionsError: null,
        selectedId: null,
        forcedOrderId: null,
        modalOpen: false,
      };
    case actionType.SET_TRANSACTIONS:
      return {
        ...state,
        transactions: action.payload,
        transactionsLoading: false,
      };
    case actionType.SET_TRANSACTIONS_LOADING:
      return {
        ...state,
        transactionsLoading: action.payload,
        transactionsError: action.payload ? null : state.transactionsError,
      };
    case actionType.SET_TRANSACTIONS_ERROR:
      return {
        ...state,
        transactionsError: action.payload,
        transactionsLoading: false,
      };

    // Picking another row discards results that belong to the previous one.
    case actionType.SET_SELECTED_TRANSACTION:
      if (action.payload === state.selectedId) return state;
      return { ...state, ...clearedEvaluation, selectedId: action.payload };

    case actionType.SET_EVALUATION:
      return {
        ...state,
        evaluation: action.payload,
        evaluationLoading: false,
      };
    case actionType.SET_EVALUATION_LOADING:
      return {
        ...state,
        ...(action.payload ? clearedEvaluation : {}),
        evaluationLoading: action.payload,
      };
    case actionType.SET_EVALUATION_ERROR:
      return {
        ...state,
        evaluationError: action.payload,
        evaluationLoading: false,
      };
    // Step back to the transaction list, keeping the VIN lookup results.
    case actionType.CLEAR_EVALUATION:
      return { ...state, ...clearedEvaluation };

    case actionType.SET_FORCE_RESULT:
      // Network failures dispatch SET_FORCE_ERROR instead and keep the modal
      // open so the user can retry.
      return {
        ...state,
        forceResult: action.payload,
        // A successful force closes the order for good. A business rejection
        // (`success: false`) arrives through this action too, and must stay
        // retryable.
        forcedOrderId:
          action.payload && action.payload.success && state.evaluation
            ? state.evaluation.orderId
            : state.forcedOrderId,
        forceLoading: false,
        modalOpen: false,
      };
    case actionType.SET_FORCE_LOADING:
      return {
        ...state,
        forceLoading: action.payload,
        ...(action.payload ? { forceResult: null, forceError: null } : {}),
      };
    case actionType.SET_FORCE_ERROR:
      return { ...state, forceError: action.payload, forceLoading: false };
    case actionType.SET_MODAL_OPEN:
      // Opening the modal starts a fresh attempt, so the error from the
      // previous one is dropped; closing it preserves the error.
      return {
        ...state,
        modalOpen: action.payload,
        forceError: action.payload ? null : state.forceError,
      };

    // Start over, but keep the product list — it is loaded once per mount.
    case actionType.RESET:
      return {
        ...initialState,
        products: state.products,
        productsLoading: state.productsLoading,
        productsError: state.productsError,
      };
    default:
      return state;
  }
};

export default reducer;
